import { useEffect, useRef, useState } from 'react';
import { cx } from '../../design/index.js';

/**
 * A salon's Saturday, as its diary shows it.
 *
 * The thing a salon owner already looks at fifty times a day, rather than a
 * picture of a robot or a phone: two people, their appointments in rows of a
 * quarter of an hour, and at 11:02 a call that nobody can take — Chris is
 * mid-cut, Inès mid-colour. On load it rings, GSI Voice takes it, and a 16:00
 * appointment appears in Chris's column. That is the product, and it is the
 * only motion on the page (`index.css`, `.diary-*`).
 *
 * ## An example, and it says so
 *
 * Every name and booking here is invented, so the caption says it is an
 * example, in plain view (ADR-0020 §5). The grid is `aria-hidden`: a screen
 * reader gets one sentence describing what happens, not forty boxes.
 *
 * ## Nothing drawn over what it shows
 *
 * The call is a line across both columns at 11:02, and its label sits above
 * that line inside Inès's column, over the empty end of her colour: it may
 * wrap, never spill into Chris's column. Jordan's cut starts at 10:15 so that
 * at 11:02 the line runs under its text rather than through it. A line that
 * crosses a client's name reads as a crossed-out appointment, and a label over
 * « Barbe, Kévin » hides the very diary the page is showing (e2e,
 * `layout.spec.ts`).
 *
 * ## Placement without a style attribute
 *
 * The bundle ships under `style-src 'self'`, so nothing can be positioned with
 * `style="grid-row: …"`. Every placement below is a whole class string written
 * out in the source, which is also what Tailwind needs to find it: a class
 * assembled from a number at runtime would never be generated.
 */

interface Booking {
  /** Grid placement: column, first row and number of rows, as literal classes. */
  readonly area: string;
  readonly service: string;
  readonly client: string;
  /** Half an hour: one line, the client after a comma, since two would not fit. */
  readonly short?: true;
}

/*
  Rows are quarters of an hour from 9:00 (row 1) to 18:00 (row 37).
  Chris is the second column, Inès the third.
*/
const BOOKINGS: readonly Booking[] = [
  { area: 'col-start-2 row-start-[1] row-span-2', service: 'Dégradé', client: 'Marc', short: true },
  { area: 'col-start-2 row-start-[3] row-span-3', service: 'Coupe et barbe', client: 'Yann' },
  // 10:15 to 11:15: busy at 11:02, with its text clear of the call's line.
  { area: 'col-start-2 row-start-[6] row-span-4', service: 'Coupe homme', client: 'Jordan' },
  { area: 'col-start-2 row-start-[10] row-span-2', service: 'Barbe', client: 'Kévin', short: true },
  { area: 'col-start-2 row-start-[21] row-span-3', service: 'Coupe et barbe', client: 'Lucas' },
  {
    area: 'col-start-2 row-start-[25] row-span-2',
    service: 'Dégradé',
    client: 'Enzo',
    short: true,
  },
  {
    area: 'col-start-2 row-start-[33] row-span-2',
    service: 'Coupe enfant',
    client: 'Noah',
    short: true,
  },
  // 9:00 to 11:30: the label of the call sits over the end of it, clear of
  // its text even when the label wraps to two lines.
  { area: 'col-start-3 row-start-[1] row-span-10', service: 'Couleur', client: 'Maëva' },
  {
    area: 'col-start-3 row-start-[11] row-span-2',
    service: 'Brushing',
    client: 'Sarah',
    short: true,
  },
  { area: 'col-start-3 row-start-[23] row-span-4', service: 'Coupe et brushing', client: 'Léa' },
  { area: 'col-start-3 row-start-[28] row-span-8', service: 'Nattes collées', client: 'Kenza' },
];

/** The hour lines, and the label in the margin beside each. */
const HOURS: readonly (readonly [string, string])[] = [
  ['row-start-[1]', '9 h'],
  ['row-start-[5]', '10 h'],
  ['row-start-[9]', '11 h'],
  ['row-start-[13]', '12 h'],
  ['row-start-[17]', '13 h'],
  ['row-start-[21]', '14 h'],
  ['row-start-[25]', '15 h'],
  ['row-start-[29]', '16 h'],
  ['row-start-[33]', '17 h'],
];

const HALF_HOURS: readonly string[] = [
  'row-start-[3]',
  'row-start-[7]',
  'row-start-[11]',
  'row-start-[15]',
  'row-start-[19]',
  'row-start-[23]',
  'row-start-[27]',
  'row-start-[31]',
  'row-start-[35]',
];

/** Three columns: the margin for the hours, then one per person. */
const COLUMNS = 'grid grid-cols-[2.5rem_1fr_1fr] gap-x-1.5 sm:grid-cols-[2.75rem_1fr_1fr]';

/**
 * When the moment plays.
 *
 * still is the finished diary, and it is what everyone gets by default: no
 * JavaScript, a browser without IntersectionObserver, or a person who asked
 * for less motion. Otherwise the diary starts rmed — the call not yet
 * taken, the 16:00 slot empty — and plays once, when half of it is on screen.
 * On a phone it sits below the fold, and a moment played where nobody can see
 * it is a moment wasted.
 */
type Stage = 'still' | 'armed' | 'playing';

function initialStage(): Stage {
  if (!('IntersectionObserver' in window)) return 'still';
  return window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'armed' : 'still';
}

export function SalonDiary(): React.JSX.Element {
  const figure = useRef<HTMLElement>(null);
  const [stage, setStage] = useState<Stage>(initialStage);

  useEffect(() => {
    const element = figure.current;
    if (stage !== 'armed' || element === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setStage('playing');
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [stage]);

  return (
    <figure ref={figure} data-diary={stage} className="w-full">
      <div className="rounded-[14px] border border-wall-line bg-surface px-3 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
        <div aria-hidden="true">
          <p className="sign text-sign-sm text-ink">Samedi</p>
          <div className={cx(COLUMNS, 'mt-2 items-end border-b border-wall-line pb-2')}>
            <span />
            <span className="truncate text-ui-sm font-medium text-ink">Chris</span>
            <span className="truncate text-ui-sm font-medium text-ink">Inès</span>
          </div>

          <div
            className={cx(
              COLUMNS,
              // A little shorter beside the promise on a wide screen, so the
              // call and the 16:00 it made both fit a laptop's first screen.
              'relative mt-3 grid-rows-[repeat(36,0.8125rem)] sm:grid-rows-[repeat(36,0.875rem)] lg:grid-rows-[repeat(36,0.75rem)]',
            )}
          >
            {HOURS.map(([row, label]) => (
              <span
                key={label}
                className={cx('col-start-2 col-end-4 border-t border-wall-line', row)}
              />
            ))}
            {HALF_HOURS.map((row) => (
              <span key={row} className={cx('col-start-2 col-end-4 border-t border-line', row)} />
            ))}
            {HOURS.map(([row, label]) => (
              <span
                key={`label-${label}`}
                className={cx(
                  'col-start-1 -translate-y-1/2 self-start text-caption text-ink-muted figures',
                  row,
                )}
              >
                {label}
              </span>
            ))}

            <span className="col-start-2 col-end-4 row-start-[15] row-span-4 self-center text-center text-caption text-ink-muted">
              Pause déjeuner
            </span>

            {BOOKINGS.map((booking) => (
              <span
                key={booking.area}
                data-booking=""
                className={cx(
                  'my-px flex min-w-0 flex-col overflow-hidden rounded-[6px] border-l-2 border-ink-muted bg-surface-muted px-1.5 py-0.5 sm:px-2',
                  booking.area,
                )}
              >
                {booking.short === true ? (
                  <span className="truncate text-caption leading-tight text-ink">
                    <span className="font-medium">{booking.service}</span>
                    <span className="text-ink-muted">, {booking.client}</span>
                  </span>
                ) : (
                  <>
                    <span className="truncate text-caption font-medium leading-tight text-ink">
                      {booking.service}
                    </span>
                    <span className="truncate text-caption leading-tight text-ink-muted">
                      {booking.client}
                    </span>
                  </>
                )}
              </span>
            ))}

            {/* The appointment the call made, at 16:00 with Chris. */}
            <span
              data-booking=""
              className="diary-booked col-start-2 row-start-[29] row-span-3 my-px flex min-w-0 flex-col overflow-hidden rounded-[6px] border-l-2 border-teal-ink bg-teal-wash px-1.5 py-0.5 sm:px-2"
            >
              <span className="truncate text-caption font-medium leading-tight text-ink">
                Coupe et barbe
              </span>
              <span className="truncate text-caption leading-tight text-teal-ink">
                Par GSI Voice
              </span>
            </span>

            {/*
              The call, at 11:02 — two minutes into the 11:00 row. The ringing
              and the taken versions sit on top of each other; only one is
              visible at a time, and the taken one is what remains.
            */}
            <span className="relative z-10 col-start-2 col-end-4 row-start-[9] mt-[2px] self-start">
              <span className="diary-call-line absolute inset-x-0 top-0 block h-0.5">
                <span className="diary-ringing absolute inset-0 bg-ringing" />
                <span className="diary-taken absolute inset-0 bg-teal-ink" />
              </span>
              <span className="absolute -left-1 -top-[3px] h-2 w-2">
                <span className="diary-ringing diary-ringing-dot absolute inset-0 rounded-pill bg-ringing" />
                <span className="diary-taken absolute inset-0 rounded-pill bg-teal-ink" />
              </span>
              {/*
                Above the line, inside Inès's column: half the width less half
                the gap. On a phone the label wraps rather than spill over
                Chris's appointments.
              */}
              <span
                data-diary-label=""
                className="absolute bottom-1 right-0 grid max-w-[calc(50%-0.1875rem)] text-caption leading-tight"
              >
                <span className="diary-ringing col-start-1 row-start-1 self-end justify-self-end rounded-[6px] border border-wall-line bg-surface px-2 py-0.5 text-right text-ink">
                  11 h 02, personne ne décroche
                </span>
                <span className="diary-taken col-start-1 row-start-1 self-end justify-self-end rounded-[6px] border border-teal-ink bg-surface px-2 py-0.5 text-right font-medium text-teal-ink">
                  11 h 02, appel pris par GSI Voice
                </span>
              </span>
            </span>
          </div>
        </div>
      </div>

      <figcaption className="mt-3 text-caption text-ink-muted">
        Exemple de journée. Les rendez-vous affichés sont fictifs.
        <span className="sr-only">
          {' '}
          L’agenda d’un samedi. À 11 h 02, Chris coupe et Inès fait une couleur : personne ne
          décroche. GSI Voice prend l’appel et réserve une coupe et barbe à 16 h avec Chris.
        </span>
      </figcaption>
    </figure>
  );
}
